import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { AsplanBundle } from '../../components/asplan/types';
import { hoechsteEinstufung, klassenLabel, legendeFuer } from '../../components/asplan/einstufung';
import {
    anlagenZeilen, offenerNachweisHinweis,
} from '../../components/asplan/ablage/anlagen';
import {
    NOTFALL_BLOCK_TITEL, RETTUNGSKETTE_KOPF, notfallAngaben, notfallLuecke,
    notfallZeilen, rettungskette,
    type NotfallBlock, type NotrufplanEingabe,
} from '../../components/asplan/ablage/notfallangaben';
import { gefahrstoff } from '../../components/asplan/kataloge/gefahrstoffe';
import { PRUEFLISTE } from '../../components/asplan/kataloge/pruefliste';
import {
    bausteineDesKapitels, wortlaut,
} from '../../components/asplan/kataloge/bausteine';
import { zeichneSchleuse, SCHLEUSE_STANDARD } from '../../components/asplan/skizzen/schleuse';
import { zeichneRaster } from '../../components/asplan/skizzen/raster';
import { ZONENPLAN_STANDARD, zeichneZonenplan } from '../../components/asplan/skizzen/zonenplan';
import { ABLAUF_STANDARD, zeichneAblauf } from '../../components/asplan/skizzen/ablauf';
import type { Zeichnung } from '../../components/asplan/skizzen/primitive';
import type { SchleusenParameter, SkizzenTyp } from '../../components/asplan/types';
import {
    A4_GEOMETRY,
    CompanyInfo,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
} from './letterhead';
import { skizzenMassstab, zeichneNachPdf } from './skizzeNachPdf';
import { sichereTextausgabe } from './winAnsi';
import { FEHLT, NICHT_ANGEGEBEN } from './fehlendeAngabe';

/**
 * Der A+S-Plan als PDF.
 *
 * ═══ WAS AUF DAS BLATT MUSS — UND WAS DAS BLATT UEBER SICH SAGEN MUSS ═════
 * Der Plan ist ein Dokument, nach dem gearbeitet wird und an dem sich im
 * Streitfall bemisst, ob jemand richtig ausgeruestet war. Deshalb steht hier
 * NICHT nur das Geregelte: Felder ohne Probe, Bereiche ohne Taetigkeits-
 * beurteilung und Proben unter Analytik-Vorbehalt erscheinen ausdruecklich.
 *
 * Ein Plan, der nur das Fertige zeigt, wird als „geprueft und ohne weiteren
 * Befund" gelesen — und genau das waere falsch, solange etwas offen ist.
 * Dieselbe Ueberlegung wie beim Koordinierungsbericht.
 *
 * ═══ GLIEDERUNG ══════════════════════════════════════════════════════════
 * Sie folgt ANHANG 3 der DGUV Regel 101-004 („Muster fuer Gliederung und
 * Inhalte des Arbeits- und Sicherheitsplanes"), nicht dem Aufbau eines
 * zufaellig vorliegenden Plans. Vor allem: die taetigkeitsbezogene
 * Gefaehrdungsbeurteilung (Anhang 3 Nr. 5) bekommt ein EIGENES Kapitel,
 * zwischen Arbeitsbereichen und Schutzmassnahmen — an genau der Stelle, an
 * der in der Praxis der Sprung vom Stoffspektrum direkt zur PSA passiert.
 *
 * ═══ BRIEFKOPF ═══════════════════════════════════════════════════════════
 * Ausschliesslich ueber `letterhead.ts` (createLetterhead / drawPage1Header /
 * drawFooters), wie Rechnung, Angebot, Einweisung und Koordinierungsbericht.
 * Umgeblaettert wird nur ueber `lh.addPage()`, damit ein hinterlegtes
 * Briefpapier auf JEDER Seite landet; die Tabellen bekommen zusaetzlich
 * `willDrawPage`, weil autoTable seine Folgeseiten selbst anlegt.
 *
 * ═══ KEIN STRICH FUER FEHLENDE WERTE ═════════════════════════════════════
 * Eine leere Zelle wird benannt (`fehlendeAngabe.ts`): „nicht angegeben",
 * in schmalen Spalten „fehlt". Ein „—" an dieser Stelle laese sich als
 * „geprueft, nichts" — in einer Befundspalte als Entwarnung. Der Strich
 * bleibt nur dort, wo er wirklich „entfaellt" heisst (Bestaetigung einer
 * AUFstufung, siehe `Stufung.bestaetigt_von`).
 */

// ─── Typografie ─────────────────────────────────────────────────────────────

const SCHRIFT = {
    titel: 16,
    untertitel: 11,
    kapitel: 12,
    unterkapitel: 10,
    block: 9.5,
    klein: 8,
    winzig: 7,
};

const ZEILE = {
    text: 4.6,
    klein: 3.6,
    kapitel: 7,
};

const GRAU: [number, number, number] = [100, 116, 139];
const SCHWARZ: [number, number, number] = [15, 23, 42];
const ROT: [number, number, number] = [190, 24, 60];

/** Ueberschrift des Blattes — auch fuer Dateinamen und Betreffzeilen. */
export const ASPLAN_TITEL = 'Arbeits- und Sicherheitsplan (A+S-Plan)';

export interface AsplanPdfOptionen {
    projektName?: string;
    projektNummer?: string;
    bauvorhabenAdresse?: string;
    ort?: string;
    datumIso?: string;
    /** Parameter der Schleusenskizze; ohne sie wird die Standardanlage gezeichnet. */
    schleuse?: SchleusenParameter;
    /**
     * Der Notrufplan des SiGe-Plans — fuer die Rettungskette in Kapitel 7.
     *
     * Genau wie `schleuse` eine Eingabe des Aufrufers und KEIN zweiter
     * Ladeweg: der Container liest ihn ohnehin schon (`useProjektBestand`),
     * und der Notrufplan bleibt dort, wo er gepflegt wird. Fehlt die Eingabe,
     * sagt das Kapitel ausdruecklich, dass die Kette nicht belegt ist — es
     * behauptet nicht, es gaebe keine Stelle (`notfallangaben.ts`).
     */
    notrufplan?: NotrufplanEingabe;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten.
     *
     * Nur fuer Tests: nur so laesst sich pruefen, dass das Briefpapier auf
     * JEDER Seite genau einmal gestempelt wird. Dasselbe Muster wie im
     * Rechnungs-, Einweisungs- und Koordinierungsgenerator.
     */
    __docFactory?: () => jsPDF;
}

// ═══════════════════════════════════════════════════════════════════════════
// BRIEFKOPF OHNE ZAHLUNGS- UND STEUERANGABEN
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Merkmale, an denen eine Gruppe der Kopf-Infospalte als Zahlungs- oder
 * Steuerangabe zu erkennen ist.
 *
 * Bewusst breit gefasst und nicht auf die eigene Bank zugeschnitten: der
 * Briefkopf ist frei konfigurierbar, und ein Filter, der nur „HASPA" kennt,
 * versagt beim naechsten Bankwechsel still.
 */
const ZAHLUNGSMERKMALE = [
    'bankverbindung', 'iban', 'bic', 'swift', 'kreditinstitut', 'kontonummer',
    'blz', 'bankleitzahl', 'sparkasse', 'volksbank', 'haspa',
    'steuernummer', 'steuer-nr', 'st.-nr', 'st-nr', 'ust-id', 'ust id',
    'umsatzsteuer', 'finanzamt', 'vat',
];

const istZahlungszeile = (zeile: string): boolean => {
    const k = zeile.toLowerCase();
    return ZAHLUNGSMERKMALE.some(m => k.includes(m));
};

/**
 * Entfernt Bankverbindung und Steuernummern aus der Kopf-Infospalte.
 *
 * ═══ WARUM DAS HIER STEHT UND NICHT IM BRIEFKOPF ══════════════════════════
 * Der A+S-Plan ist kein Geschaeftsbrief. Bankverbindung, Steuernummer und
 * USt-IdNr. sind Pflichtangaben fuer RECHNUNGEN — auf einem Dokument, nach
 * dem auf der Baustelle gearbeitet wird, stehen sie nur im Weg und lenken von
 * dem ab, was oben rechts wirklich gebraucht wird: wie man den Koordinator
 * erreicht.
 *
 * Deshalb wird hier gefiltert und NICHT der gemeinsame Briefkopf geaendert —
 * Rechnung, Gutschrift und Angebot brauchen die Angaben weiterhin, und eine
 * Aenderung an `letterhead.ts` traefe sie alle.
 *
 * ═══ WIE GEFILTERT WIRD ═══════════════════════════════════════════════════
 * Die Infospalte ist in Gruppen gegliedert (Leerzeilen sind Gruppentrenner).
 * Enthaelt eine Gruppe ein Zahlungsmerkmal, faellt sie GANZ weg — sonst
 * bliebe der Bankname oder der Kontoinhaber als sinnloser Rest stehen, weil
 * in diesen Zeilen selbst kein Stichwort vorkommt.
 *
 * Steht alles in EINER Gruppe ohne Leerzeilen, wird ab der ersten
 * Trefferzeile abgeschnitten: Zahlungsangaben stehen am Ende eines
 * Briefkopfs, davor stehen Anschrift und Erreichbarkeit — und die sollen
 * bleiben.
 */
export const ohneZahlungsangaben = (company: CompanyInfo): CompanyInfo => {
    const text = company.letterheadText;
    if (!text || !text.trim()) return company;

    const zeilen = text.replace(/\r\n/g, '\n').split('\n');
    const hatLeerzeile = zeilen.some(z => !z.trim());

    if (!hatLeerzeile) {
        const ersterTreffer = zeilen.findIndex(istZahlungszeile);
        if (ersterTreffer < 0) return company;
        return { ...company, letterheadText: zeilen.slice(0, ersterTreffer).join('\n').trimEnd() };
    }

    // Gruppen bilden und die betroffenen ganz verwerfen.
    const gruppen: string[][] = [[]];
    for (const z of zeilen) {
        if (!z.trim()) gruppen.push([]);
        else gruppen[gruppen.length - 1].push(z);
    }

    const bleibt = gruppen.filter(g => g.length > 0 && !g.some(istZahlungszeile));
    return { ...company, letterheadText: bleibt.map(g => g.join('\n')).join('\n\n') };
};

// ═══════════════════════════════════════════════════════════════════════════

const renderAsplan = (
    doc: jsPDF,
    b: AsplanBundle,
    company: CompanyInfo,
    opt: AsplanPdfOptionen = {},
): void => {
    // Letzte Rueckfallebene gegen den UTF-16-Salat auf dem Papier: jsPDF
    // schreibt mit den Standardschriften in CP1252, und EIN Zeichen ausserhalb
    // dieser Kodierung kippt die GANZE Zeichenkette — sichtbar als '%%' mit
    // zerrissener Buchstabenstellung. Der A+S-Plan war als einziger der vier
    // Generatoren ungeschuetzt, und der Pfeil in „Personendurchgang schwarz ->
    // weiss" wird in JEDEM Plan gezeichnet. Muster: generatorAushang.ts.
    sichereTextausgabe(doc);

    const geo = A4_GEOMETRY;
    // Der A+S-Plan ist kein Geschaeftsbrief: Bankverbindung und Steuernummern
    // fallen aus der Kopf-Infospalte heraus (siehe ohneZahlungsangaben).
    const kopfFirma = ohneZahlungsangaben(company);
    const lh = createLetterhead(doc, kopfFirma, geo);
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, kopfFirma, lh, geo);

    // Der Inhalt beginnt unter dem tieferen der beiden Kopfbloecke — sonst
    // laeuft er in die rechte Infospalte hinein (Muster aus dem
    // Koordinierungsgenerator).
    let y = Math.max(addressFieldY, rightColBottom + 8);
    const platz = createSpaceGuard(lh, geo, () => y, v => { y = v; });
    const links = geo.margin;
    const breite = geo.contentWidth;

    // ── Hilfen ─────────────────────────────────────────────────────────────

    const setzeText = (
        text: string,
        groesse = SCHRIFT.block,
        stil: 'normal' | 'bold' | 'italic' = 'normal',
        farbe: [number, number, number] = SCHWARZ,
    ) => {
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        doc.setTextColor(...farbe);
        const zeilen = doc.splitTextToSize(text, breite) as string[];
        for (const z of zeilen) {
            platz(ZEILE.text);
            doc.text(z, links, y);
            y += ZEILE.text;
        }
    };

    const kapitel = (nr: string, titel: string) => {
        platz(ZEILE.kapitel + 8);
        y += 3;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.kapitel);
        doc.setTextColor(...SCHWARZ);
        doc.text(`${nr}  ${titel}`, links, y);
        y += 1.5;
        doc.setDrawColor(...GRAU);
        doc.setLineWidth(0.3);
        doc.line(links, y, links + breite, y);
        y += ZEILE.text + 1;
    };

    const unterkapitel = (titel: string) => {
        platz(ZEILE.text * 2);
        y += 1.5;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.unterkapitel);
        doc.setTextColor(...SCHWARZ);
        doc.text(titel, links, y);
        y += ZEILE.text;
    };

    const liste = (eintraege: string[], zeichen = '•') => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.block);
        doc.setTextColor(...SCHWARZ);
        for (const e of eintraege) {
            const zeilen = doc.splitTextToSize(e, breite - 5) as string[];
            zeilen.forEach((z, i) => {
                platz(ZEILE.text);
                if (i === 0) doc.text(zeichen, links + 1, y);
                doc.text(z, links + 5, y);
                y += ZEILE.text;
            });
        }
    };

    /** Ein Kasten fuer das, was NICHT geregelt ist. */
    const offenerPunkt = (text: string) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.klein);
        doc.setTextColor(...ROT);
        const zeilen = doc.splitTextToSize(text, breite - 5) as string[];
        for (const z of zeilen) {
            platz(ZEILE.klein);
            doc.text(z, links + 5, y);
            y += ZEILE.klein + 0.6;
        }
        doc.setTextColor(...SCHWARZ);
    };

    /**
     * Eine Tabelle im Satzspiegel.
     *
     * Die uebergebenen Spaltenbreiten werden auf die verfuegbare Breite
     * SKALIERT statt sie zu uebernehmen. Grund: autoTable braucht neben den
     * Spalten noch Platz fuer die Rahmenlinien; eine Breitenliste, die exakt
     * die Satzbreite ergibt, laeuft deshalb um ein bis zwei Millimeter ueber
     * und autoTable meldet „content could not fit page" — sichtbar wird das
     * als abgeschnittene letzte Spalte.
     *
     * Mit der Skalierung bleibt das VERHAELTNIS der Spalten erhalten, das der
     * Aufrufer gemeint hat, und die Summe stimmt immer. So kann kein
     * spaeterer Aufruf diesen Fehler wieder einbauen.
     */
    const tabelle = (kopfzeile: string[], zeilen: (string | number)[][], breiten?: number[]) => {
        if (zeilen.length === 0) return;
        const nutzbar = breite - 1;
        const skaliert = breiten && breiten.length > 0
            ? (() => {
                const summe = breiten.reduce((a, b) => a + b, 0);
                const faktor = summe > 0 ? nutzbar / summe : 1;
                return breiten.map(w => w * faktor);
            })()
            : undefined;
        autoTable(doc, {
            startY: y,
            head: [kopfzeile],
            body: zeilen.map(r => r.map(z => String(z ?? ''))),
            margin: { left: links, right: geo.margin },
            tableWidth: nutzbar,
            styles: { font: 'helvetica', fontSize: SCHRIFT.klein, cellPadding: 1.4, overflow: 'linebreak' },
            headStyles: { fillColor: [241, 245, 249], textColor: SCHWARZ, fontStyle: 'bold' },
            columnStyles: skaliert
                ? Object.fromEntries(skaliert.map((w, i) => [i, { cellWidth: w }]))
                : undefined,
            // autoTable legt Folgeseiten SELBST an. Fehlt dieser Callback,
            // bleibt jede von der Tabelle erzeugte Folgeseite ohne
            // Briefpapier — und das faellt erst beim Kunden auf.
            willDrawPage: lh.willDrawPage,
        });
        y = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
    };

    const skizze = (z: Zeichnung) => {
        const massstab = skizzenMassstab(z, breite);
        const hoehe = z.hoehe * massstab;
        platz(hoehe + 12);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.unterkapitel);
        doc.setTextColor(...SCHWARZ);
        doc.text(z.titel, links, y);
        y += ZEILE.text;
        if (z.untertitel) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(SCHRIFT.klein);
            doc.setTextColor(...GRAU);
            doc.text(z.untertitel, links, y);
            y += ZEILE.klein + 1;
        }

        zeichneNachPdf(doc, z, { x: links, y, massstab });
        y += hoehe + 2;

        if (z.legende?.length) {
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(SCHRIFT.winzig);
            doc.setTextColor(...GRAU);
            for (const l of z.legende) {
                platz(ZEILE.klein);
                doc.text(`— ${l.text}`, links, y);
                y += ZEILE.klein;
            }
        }
        for (const f of z.fussnoten ?? []) {
            doc.setFont('helvetica', 'italic');
            doc.setFontSize(SCHRIFT.winzig);
            doc.setTextColor(...GRAU);
            const zeilen = doc.splitTextToSize(f, breite) as string[];
            for (const zz of zeilen) {
                platz(ZEILE.klein);
                doc.text(zz, links, y);
                y += ZEILE.klein;
            }
        }
        doc.setTextColor(...SCHWARZ);
        y += 3;
    };

    /**
     * Druckt die aktiven Bausteine eines Kapitels.
     *
     * Ohne Wahl gilt der Katalog: ein Plan, an dem niemand etwas abgewaehlt
     * hat, enthaelt die Bausteine — sonst waere das Kapitel leer, obwohl der
     * Bearbeiter nie eine Entscheidung dagegen getroffen hat.
     */
    const bausteinKapitel = (kapitel_key: string, ueberschrift?: string) => {
        const liste = bausteineDesKapitels(kapitel_key)
            .filter(x => !x.veraltet && (!x.regelwerk || x.regelwerk === plan.regelwerk))
            .map(x => {
                const w = b.bausteine.find(
                    y => y.kapitel_key === kapitel_key && y.baustein_key === x.key);
                return { baustein: x, aktiv: w ? w.aktiv : true, override: w?.text_override ?? '' };
            })
            .filter(x => x.aktiv);

        if (liste.length === 0) return;
        if (ueberschrift) unterkapitel(ueberschrift);
        for (const x of liste) {
            setzeText(x.baustein.titel, SCHRIFT.block, 'bold');
            setzeText(wortlaut(x.baustein, x.override));
            y += 1;
        }
    };

    // ── Titelblock ─────────────────────────────────────────────────────────

    const plan = b.plan;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.setTextColor(...SCHWARZ);
    doc.text(plan.titel || ASPLAN_TITEL, links, y);
    y += ZEILE.kapitel;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(SCHRIFT.untertitel);
    doc.setTextColor(...GRAU);
    doc.text(plan.untertitel || 'gemäß DGUV Regel 101-004 / TRGS 524', links, y);
    y += ZEILE.text + 2;

    doc.setFontSize(SCHRIFT.block);
    doc.setTextColor(...SCHWARZ);
    const kopfangaben = [
        ['Bauvorhaben', opt.projektName ?? ''],
        ['Projekt-Nr.', opt.projektNummer ?? ''],
        ['Baustelle', opt.bauvorhabenAdresse ?? ''],
        ['Stand', plan.stand ? datumDeutsch(plan.stand) : NICHT_ANGEGEBEN],
        ['Fassung', plan.rev_label || 'Entwurf'],
        ['Regelwerk', plan.regelwerk === 'trgs524'
            ? 'TRGS 524'
            : `DGUV Regel 101-004 / TRGS 524 (${plan.regelwerk_fassung === '2026-07' ? 'Fassung 07/2026' : 'Vorfassung'})`],
        ['Status', plan.status === 'freigegeben'
            ? `freigegeben von ${plan.freigegeben_von_name || `(${NICHT_ANGEGEBEN})`}`
            : 'ENTWURF — nicht freigegeben'],
    ].filter(([, v]) => v);

    tabelle(['Angabe', 'Inhalt'], kopfangaben, [40, breite - 40]);

    // ── 1 Allgemeine Daten ─────────────────────────────────────────────────

    kapitel('1', 'Allgemeine Daten und Beteiligte');

    if (b.beteiligte.length > 0) {
        tabelle(
            ['Funktion', 'Organisation / Person', 'Erreichbarkeit'],
            b.beteiligte.map(p => [
                p.funktion,
                [p.organisation, p.person, p.anschrift].filter(Boolean).join('\n'),
                [p.telefon, p.mobil, p.email].filter(Boolean).join('\n'),
            ]),
            [42, 78, breite - 120],
        );
    } else {
        offenerPunkt('OFFEN: Es sind keine Beteiligten erfasst. Anhang 3 Nr. 1 verlangt '
            + 'Auftraggeber, Arbeitsschutzbehörde, Gutachter sowie den Koordinator mit '
            + 'seinen Weisungsbefugnissen.');
    }

    if (b.unternehmen.length > 0) {
        unterkapitel('Unternehmen und Aufsichtsführende');
        tabelle(
            ['Unternehmen', 'Gewerk', 'Aufsichtsführender', 'Sachkunde', 'Erklärung'],
            b.unternehmen.map(u => [
                u.unternehmen,
                u.gewerk,
                u.aufsichtsfuehrender || NICHT_ANGEGEBEN,
                u.sachkunde_vorgelegt ? `ja (${u.sachkunde_datum ?? ''})` : 'NEIN',
                u.erklaerung_status === 'zurueck' ? 'liegt vor' : 'offen',
            ]),
        );
        const ohneSachkunde = b.unternehmen.filter(u => !u.sachkunde_vorgelegt);
        if (ohneSachkunde.length > 0) {
            offenerPunkt(`OFFEN: Für ${ohneSachkunde.map(u => u.unternehmen).join(', ')} liegt `
                + 'kein Sachkundenachweis nach DGUV Regel 101-004 Anhang 6A vor. Ohne ihn darf '
                + 'niemand die Aufsicht führen.');
        }
    }

    bausteinKapitel('notwendigkeit');
    bausteinKapitel('koordinator');
    bausteinKapitel('personenkreis');
    bausteinKapitel('aufsicht');
    bausteinKapitel('gueltigkeit');
    bausteinKapitel('anzeige');

    // ── 2 Standort und Historische Erkundung ───────────────────────────────

    kapitel('2', 'Standortbeschreibung und Historische Erkundung');

    const he = plan.historische_erkundung ?? {};
    if (he.nutzungsgeschichte) {
        unterkapitel('Nutzungsgeschichte');
        setzeText(String(he.nutzungsgeschichte));
    } else {
        offenerPunkt('OFFEN: Die Historische Erkundung fehlt. Ohne sie ist die Auswahl der '
            + 'untersuchten Stoffe nicht begründbar (Anhang 3 Nr. 2).');
    }
    if (he.gebaeudeschadstoffe?.length) {
        unterkapitel('Gebäudeschadstoffe');
        liste(he.gebaeudeschadstoffe);
    }
    if (he.luecken) {
        unterkapitel('Nicht ermittelbare Angaben');
        setzeText(String(he.luecken));
        if (he.luecken_folge) {
            setzeText(`Folge: ${he.luecken_folge}`, SCHRIFT.block, 'bold');
        } else {
            offenerPunkt('OFFEN: Es ist nicht festgehalten, welche Folge aus den Lücken der '
                + 'Historischen Erkundung gezogen wird (Vorsorgeprinzip, erweiterte Analytik, '
                + 'vorlaufende Sondierung).');
        }
    }

    // ── 3 Gefahrstoffermittlung ────────────────────────────────────────────

    kapitel('3', 'Gefahrstoffermittlung und Gefahrenanalyse');

    const aktiveStoffe = plan.stoffe.filter(s => s.aktiv);
    if (aktiveStoffe.length > 0) {
        tabelle(
            ['Stoff', 'Erscheinungsform', 'Aufnahmewege', 'Warum im Plan'],
            aktiveStoffe.map(s => {
                const g = gefahrstoff(s.key);
                // Ohne Katalogeintrag gibt es keine Erscheinungsform und keine
                // Aufnahmewege — gesagt wird, warum, nicht „—".
                return [
                    g?.name ?? s.key,
                    g?.erscheinungsform.join(', ') ?? 'nicht im Katalog',
                    g?.aufnahmewege ?? 'nicht im Katalog',
                    s.hinweis || '(nicht begründet)',
                ];
            }),
            [40, 26, 58, breite - 124],
        );
    } else {
        offenerPunkt('OFFEN: Es ist kein Gefahrstoff geführt.');
    }

    if (plan.grenzwerte.length > 0) {
        unterkapitel('Bewertungsmaßstäbe (Stand der Übernahme)');
        tabelle(
            ['Stoff', 'Art', 'Wert', 'Quelle / Fassung'],
            plan.grenzwerte.map(g => [
                gefahrstoff(g.stoff_key)?.name ?? g.stoff_key,
                g.art.toUpperCase(),
                `${g.wert} ${g.einheit}`,
                `${g.quelle} · ${g.fassung}`,
            ]),
        );
        setzeText('Die Werte wurden bei der Aufstellung des Plans übernommen und gelten '
            + 'unverändert weiter. Fortschreibungen der zugrunde liegenden Regeln ändern '
            + 'sie nicht selbsttätig.', SCHRIFT.klein, 'italic', GRAU);
    }

    unterkapitel('Auswertung der Rasterfelduntersuchung');
    if (b.proben.length > 0) {
        tabelle(
            ['Feld', 'Probe', 'Boden', 'Tiefe', 'Umfang', 'Maßgebender Befund', 'Klasse'],
            b.proben.map(p => {
                const feld = b.rasterfelder.find(f => f.id === p.rasterfeld_id);
                const relevant = p.befunde.filter(x => x.medium === 'feststoff' && x.wert !== null);
                return [
                    feld?.feld_nr ?? FEHLT,
                    p.mischprobe,
                    p.zusammensetzung,
                    tiefe(p.tiefe_von, p.tiefe_bis),
                    p.untersuchungsumfang,
                    // Kein Feststoffwert erfasst heisst NICHT „nichts gefunden".
                    relevant.map(x => `${x.bezeichnung} ${x.wert} ${x.einheit}`).join('; ') || NICHT_ANGEGEBEN,
                    klassenLabel(p.einstufung, p.vorbehalt),
                ];
            }),
            [13, 17, 17, 16, 17, breite - 104, 20],
        );

        const legende = legendeFuer(b.proben.map(p => p.einstufung));
        if (legende.length > 0) {
            setzeText(
                `Legende: ${legende.map(l => `${l.label} = ${l.text}`).join('; ')}. `
                + 'Mit * gekennzeichnet: Analytik noch nicht vollständig.',
                SCHRIFT.winzig, 'italic', GRAU,
            );
        }

        const eluatOhneFeststoff = b.proben.some(p => p.befunde.some(
            x => x.medium === 'eluat' && x.einstufungsrelevant === false));
        if (eluatOhneFeststoff) {
            setzeText('Eluat-Auffälligkeiten, die nicht im Feststoff nachgewiesen wurden, sind '
                + 'nach EBV nicht einstufungsrelevant. Sie sind erfasst, gehen aber nicht in die '
                + 'Einstufung ein.', SCHRIFT.winzig, 'italic', GRAU);
        }
    } else {
        offenerPunkt('OFFEN: Es sind keine Proben erfasst.');
    }

    const vorbehalte = b.proben.filter(p => p.vorbehalt);
    if (vorbehalte.length > 0) {
        unterkapitel('Felder mit Analytik-Vorbehalt');
        liste(vorbehalte.map(p => {
            const feld = b.rasterfelder.find(f => f.id === p.rasterfeld_id);
            return `${feld ? `Feld ${feld.feld_nr}` : 'Feld nicht zugeordnet'}, Probe ${p.mischprobe}: ${p.vorbehalt_grund || 'Analytik unvollständig'}`
                + (p.wiedervorlage_am ? ` — Wiedervorlage ${datumDeutsch(p.wiedervorlage_am)}` : ' — OHNE Wiedervorlage');
        }));
        setzeText('Bis zum Vorliegen der Ergebnisse wird nach dem Vorsorgeprinzip gearbeitet.',
            SCHRIFT.block, 'bold');
    }

    const rasterZeichnung = rasterAusBundle(b);
    if (rasterZeichnung) skizze(rasterZeichnung);

    bausteinKapitel('plausibilitaet', 'Plausibilisierung der Probennahmestrategie');

    // ── 4 Arbeitsbereiche ──────────────────────────────────────────────────

    kapitel('4', 'Arbeitsbereichsanalyse');

    if (b.cluster.length > 0) {
        tabelle(
            ['Bereich', 'Bezeichnung', 'Felder', 'Art', 'Einstufung', 'Schutzklasse'],
            b.cluster.map(c => [
                c.kennung,
                c.bezeichnung || c.baumassnahme,
                c.felder.join(', ') || NICHT_ANGEGEBEN,
                artText(c.art),
                c.leitparameter || NICHT_ANGEGEBEN,
                c.art === 'abgegrenzt' ? 'nicht A+S-pflichtig' : (c.schutzklasse_key || 'OFFEN'),
            ]),
            [16, 44, 24, 26, 30, breite - 140],
        );

        for (const c of b.cluster) {
            if (!c.befundlage && !c.aushubstrategie && !c.besonderheit) continue;
            unterkapitel(`Bereich ${c.kennung} — ${c.bezeichnung || c.baumassnahme}`);
            if (c.befundlage) setzeText(`Befundlage: ${c.befundlage}`);
            if (c.aushubstrategie) setzeText(`Arbeitsweise: ${c.aushubstrategie}`);
            if (c.besonderheit) setzeText(`Besonderheit: ${c.besonderheit}`);
            if (c.schutzklasse_begruendung) {
                setzeText(`Begründung der Schutzklasse: ${c.schutzklasse_begruendung}`,
                    SCHRIFT.block, 'bold');
            }
        }

        const ohneKlasse = b.cluster.filter(c => c.art !== 'abgegrenzt' && !c.schutzklasse_key);
        if (ohneKlasse.length > 0) {
            offenerPunkt(`OFFEN: Für ${ohneKlasse.map(c => c.kennung).join(', ')} ist keine `
                + 'Schutzklasse festgelegt.');
        }
    } else {
        offenerPunkt('OFFEN: Es ist kein Arbeitsbereich abgegrenzt (Anhang 3 Nr. 4).');
    }

    // ── 5 Gefährdungsbeurteilung ───────────────────────────────────────────

    kapitel('5', 'Tätigkeitsbezogene Gefährdungsbeurteilung');
    setzeText('Anhang 3 Nr. 5 der DGUV Regel 101-004: Zusammenführung der stofflichen, '
        + 'arbeitsbereichs- und tätigkeitsbezogenen Faktoren zu einer halbquantitativen '
        + 'Expositionsabschätzung. Leitfrage: wer macht wann was wie, wie oft und wie lange.',
        SCHRIFT.klein, 'italic', GRAU);

    if (b.taetigkeiten.length > 0) {
        tabelle(
            ['Bereich', 'Tätigkeit / Verfahren', 'Wer', 'Dauer', 'Wege', 'Exposition', 'SK'],
            // Schmale Spalten: Kurzform „fehlt". Die Schutzklasse heisst wie in
            // der Bereichstabelle „OFFEN" — sie ist eine Entscheidung, die
            // noch aussteht, keine Angabe, die jemand vergessen hat.
            b.taetigkeiten.map(t => [
                t.cluster_kennung || FEHLT,
                [t.taetigkeit, t.arbeitsverfahren].filter(Boolean).join('\n'),
                t.personenkreis || t.rolle_key || FEHLT,
                [t.dauer_min ? `${t.dauer_min} min` : '', t.haeufigkeit].filter(Boolean).join('\n') || FEHLT,
                t.expositionswege.length > 0
                    ? t.expositionswege.map(w => `${w.weg}: ${w.relevant ? 'ja' : 'nein'}`).join('\n')
                    : 'nicht geprüft',
                t.exposition ?? 'offen',
                t.schutzklasse_key || 'OFFEN',
            ]),
            [18, breite - 132, 22, 20, 28, 24, 18],
        );

        const ungeprueft = b.taetigkeiten.filter(t => t.expositionswege.length === 0);
        if (ungeprueft.length > 0) {
            offenerPunkt(`OFFEN: Bei ${ungeprueft.length} Tätigkeit(en) ist nicht geprüft, über `
                + 'welchen Weg eine Exposition besteht.');
        }
    } else {
        offenerPunkt('OFFEN: Es ist keine Tätigkeit beurteilt. Ohne diesen Schritt springt der '
            + 'Plan vom Stoffspektrum unmittelbar zu den Schutzmaßnahmen.');
    }

    bausteinKapitel('wirkungspfade', 'Wirkungspfade');

    // ── 6 Schutzmaßnahmen ──────────────────────────────────────────────────

    kapitel('6', 'Arbeits- und Gesundheitsschutz');
    setzeText('Technische und organisatorische Maßnahmen haben Vorrang vor persönlicher '
        + 'Schutzausrüstung. Erst wenn beide keine ausreichende Sicherheit bieten, greift die '
        + 'PSA (STOP-Prinzip).');

    unterkapitel('6.1  Schutzklassen');
    for (const sk of [...b.schutzklassen].sort((x, z) => x.rang - z.rang)) {
        platz(ZEILE.text * 4);
        setzeText(`${sk.key} — ${sk.bezeichnung}`, SCHRIFT.block, 'bold');
        if (sk.anwendungsbereich) setzeText(sk.anwendungsbereich, SCHRIFT.klein);
        if (sk.psa.length) liste(sk.psa.map(p => p.text), '–');
        if (sk.atemschutz.geraet) {
            setzeText(
                `Atemschutz: ${sk.atemschutz.geraet}`
                + (sk.atemschutz.filtertyp ? ` · Filter ${sk.atemschutz.filtertyp}` : '')
                + (sk.atemschutz.wechsel_standard ? ` · Wechsel: ${sk.atemschutz.wechsel_standard}` : ''),
                SCHRIFT.klein,
            );
            if (sk.tragezeit?.ergebnis_min) {
                // Fehlt die Erholungsdauer, sagt der Satz das — „mindestens —
                // Minuten" laese sich wie eine Zahl, die nur schlecht gedruckt ist.
                const erholung = sk.tragezeit.erholung_min != null
                    ? `danach mindestens ${sk.tragezeit.erholung_min} Minuten Erholung`
                    : `danach Erholung (Dauer ${NICHT_ANGEGEBEN})`;
                setzeText(
                    `Tragezeitbegrenzung: höchstens ${sk.tragezeit.ergebnis_min} Minuten je Einsatz, `
                    + erholung
                    + (sk.tragezeit.korrekturen?.length
                        ? ` (berücksichtigt: ${sk.tragezeit.korrekturen.map(k => k.grund).join(', ')})`
                        : ''),
                    SCHRIFT.klein, 'bold',
                );
            } else {
                offenerPunkt(`OFFEN: Für ${sk.key} ist keine Tragezeitbegrenzung nach DGUV Regel `
                    + '112-190 Anhang 2 ermittelt.');
            }
        }
        if (sk.zusatz.length) liste(sk.zusatz.map(z => z.text), '–');
        y += 1.5;
    }

    bausteinKapitel('technisch', '6.1a  Technische Schutzmaßnahmen');
    bausteinKapitel('organisatorisch', '6.1b  Organisatorische Schutzmaßnahmen');
    bausteinKapitel('hygiene', '6.1c  Verhaltensregeln');
    bausteinKapitel('arbeitsmedizin', '6.1d  Arbeitsmedizinische Vorsorge');
    bausteinKapitel('beschaeftigung', '6.1e  Beschäftigungsbeschränkungen');
    bausteinKapitel('unterweisung', '6.1f  Unterweisung');
    bausteinKapitel('explosionsschutz', '6.1g  Explosionsschutz und Heißarbeiten');

    unterkapitel('6.2  Auf- und Abstufung der Schutzklasse');
    setzeText('Aufstufen: durch den Aufsichtsführenden, sofort im Anlassfall (Geruch, '
        + 'Verfärbung, unbekanntes Material, überschrittener Schwellenwert), Eintrag im '
        + 'Bautagebuch. Abstufen: nur durch den Koordinator nach DGUV Regel 101-004, '
        + 'schriftlich und nur auf Nachweis — hergestellter Weißbereich oder Messreihe über '
        + 'mehrere aufeinanderfolgende Schichten.');

    if (b.stufungen.length > 0) {
        tabelle(
            ['Datum', 'Bereich', 'Richtung', 'von – nach', 'Anlass / Grundlage', 'Veranlasst', 'Bestätigt'],
            b.stufungen.map(s => [
                s.entschieden_am ? datumDeutsch(s.entschieden_am) : NICHT_ANGEGEBEN,
                s.cluster_kennung + (s.bereich_hinweis ? ` (${s.bereich_hinweis})` : ''),
                s.richtung === 'auf' ? 'Aufstufung' : 'Abstufung',
                `${s.von_key} – ${s.nach_key}`,
                s.anlass || s.grundlage || NICHT_ANGEGEBEN,
                s.entschieden_von || NICHT_ANGEGEBEN,
                // Bei der AUFstufung ist keine Bestaetigung vorgesehen — der
                // Strich heisst hier „entfaellt", nicht „fehlt" (siehe Typ).
                s.bestaetigt_von || (s.richtung === 'ab' ? 'FEHLT' : '—'),
            ]),
        );
    }

    unterkapitel('6.3  Rollen und Arbeitsschritte');
    if (b.rollen.length > 0) {
        tabelle(
            ['Rolle', 'Bereich', 'Arbeitsschritte in Reihenfolge', 'Schutzklasse je Situation'],
            b.rollen.map(r => [
                r.rolle,
                r.cluster.join(', ') || FEHLT,
                r.schritte.map(s => `${s.nr}. ${s.text}`).join('\n') || NICHT_ANGEGEBEN,
                r.situationen.map(s =>
                    `${s.ort}: ${s.schutzklasse_key}${s.bereitstellung ? ' (auf Bereitstellung)' : ''}`,
                ).join('\n') || NICHT_ANGEGEBEN,
            ]),
            [34, 18, breite - 122, 70],
        );
    } else {
        offenerPunkt('OFFEN: Es sind keine Rollen mit ihren Arbeitsschritten beschrieben.');
    }

    // Die Schwarz-Weiss-Anlage ist der technische Regelfall — ihr Schema
    // gehoert auch dann in den Plan, wenn niemand sie erfasst hat, denn ohne
    // sie fehlt die Aussage, ueber die der kontaminierte Bereich verlassen
    // wird. Ihre MASSE sind dagegen eine oertliche Festlegung: aus der
    // Vorbelegung gedruckt saehen „2 m, 2 m, 2 m, 6 m, 3 m" aus wie eine
    // Planung, die niemand getroffen hat. Erfasst heisst hier dasselbe wie
    // beim Zonenplan: ein GESPEICHERTER Parametersatz. `opt.schleuse` allein
    // genuegt nicht — die Oberflaeche fuehrt dort auch die unveraenderte
    // Vorbelegung mit.
    unterkapitel('6.4  Schwarz-Weiß-Anlage');
    const schleuseErfasst = skizzenParameter(b, 'schleuse', SCHLEUSE_STANDARD) !== null;
    skizze(zeichneSchleuse(opt.schleuse ?? SCHLEUSE_STANDARD, { masse: schleuseErfasst }));
    if (!schleuseErfasst) {
        offenerPunkt('OFFEN: Für diese Baustelle ist keine Schwarz-Weiß-Anlage erfasst. '
            + 'Gezeigt ist die Regelausführung ohne Maße; Kammerzahl, Abmessungen, '
            + 'Ausstattung und Aufstellort sind vor Einrichtung festzulegen.');
    }

    // Zonenplan NUR, wenn er erfasst ist. Seine Vorbelegung enthaelt
    // ortsbezogene Angaben — Absperrung, BE-Flaeche, Zwischenlager —, die
    // niemand fuer DIESE Baustelle getroffen hat; gedruckt saehen sie aus wie
    // eine Festlegung. Bei der Schwarz-Weiss-Anlage wird nur die Bemassung
    // weggelassen (siehe oben) — die Abfolge schwarz → grau → weiss ist der
    // Regelfall und keine ortsbezogene Angabe.
    const zonen = skizzenParameter(b, 'zonenplan', ZONENPLAN_STANDARD);
    if (zonen) {
        unterkapitel('6.5  Zonenplan');
        skizze(zeichneZonenplan(zonen));
    }

    // ── 7 Messkonzept ──────────────────────────────────────────────────────

    kapitel('7', 'Messkonzept zur Überwachung der Arbeitsplatzbedingungen');
    if (plan.messkonzept.length > 0) {
        tabelle(
            ['Messziel', 'Parameter', 'Maßstab', 'Verfahren / Gerät', 'Häufigkeit', 'Schwelle / Maßnahme'],
            plan.messkonzept.map(m => [
                messzielText(m.messziel),
                m.parameter,
                m.massstab,
                [m.methodik, m.geraet, m.kalibrierung].filter(Boolean).join('\n'),
                m.haeufigkeit,
                [m.alarmschwelle || m.schwellenwert, m.massnahme].filter(Boolean).join('\nMaßnahme: '),
            ]),
            [30, 26, 26, 34, 22, breite - 138],
        );
        setzeText('Die Methoden der TRGS 402 sind bei den wechselnden Bedingungen '
            + 'kontaminierter Bereiche nicht anwendbar (TRGS 524 Anlage 9).',
            SCHRIFT.winzig, 'italic', GRAU);
    } else {
        offenerPunkt('OFFEN: Es ist kein Messkonzept festgelegt (Anhang 3 Nr. 7).');
    }

    // ── Notfall, Erste Hilfe, Brandschutz ──────────────────────────────────
    //
    // Die Textbausteine sagen, WIE verfahren wird — sie enthalten
    // ausdruecklich nichts Projektbezogenes (Regel 8, im Katalog geprueft).
    // Wo der Sammelplatz liegt, wie breit die Rettungszufahrt ist und unter
    // welcher Nummer der Koordinator erreichbar ist, steht in
    // `asplaene.notfall` und wird HIER dazugedruckt. Ein Aushang, der den
    // Sammelplatz nennt, und ein Planblatt, das dazu schweigt, widersprechen
    // einander — im Ernstfall gilt dann, was gerade greifbar ist.
    //
    // Welches Feld in welchen Abschnitt gehoert, was als vorhanden gilt und
    // wie die Luecke heisst, entscheidet `ablage/notfallangaben.ts`; gelesen
    // wird ueber `notfallWert` aus `bestandUebernahme.ts`. Der Generator
    // druckt nur.
    const notfallBlock = (block: NotfallBlock) => {
        const angaben = notfallAngaben(plan.notfall, block);
        unterkapitel(NOTFALL_BLOCK_TITEL[block]);
        tabelle(['Angabe', 'Festlegung dieser Baustelle'], notfallZeilen(angaben), [56, 114]);
        const luecke = notfallLuecke(angaben);
        if (luecke) offenerPunkt(luecke);
    };

    // Die Rettungskette steht seit jeher auf dem Aushang „Rettungskette",
    // aber nicht auf dem Planblatt — obwohl Anhang 3 sie namentlich verlangt.
    // Ein Plan, dessen Aushang die Klinik nennt und dessen Blatt dazu
    // schweigt, widerspricht sich; im Ernstfall gilt dann, was gerade
    // greifbar ist. Quelle ist der Notrufplan des SiGe-Plans, mitgegeben vom
    // Container — hier wird nichts nachgeladen und nichts kopiert. Was als
    // Stelle zaehlt und wie die Luecke heisst, steht in
    // `ablage/notfallangaben.ts`; der Generator druckt nur.
    const rettungsketteBlock = () => {
        const kette = rettungskette(opt.notrufplan);
        unterkapitel('Rettungskette: Krankenhaus, Durchgangsarzt, weitere Stellen');
        tabelle(RETTUNGSKETTE_KOPF, kette.zeilen, [24, 36, 44, 26, 18, 20]);
        if (kette.luecke) {
            offenerPunkt(kette.luecke);
        } else {
            setzeText('Quelle ist der Notrufplan des SiGe-Plans; geändert wird er dort. '
                + 'Nach jedem Wechsel des Durchgangsarztes ist dieses Kapitel neu zu drucken.',
                SCHRIFT.winzig, 'italic', GRAU);
        }
    };

    bausteinKapitel('notfall', 'Notfallorganisation');
    notfallBlock('notruf');
    bausteinKapitel('erste_hilfe', 'Erste Hilfe und Rettung');
    notfallBlock('rettung');
    rettungsketteBlock();
    bausteinKapitel('brandschutz', 'Brandschutz');
    notfallBlock('brandschutz');
    bausteinKapitel('hautschutz', 'Hautschutz');

    // ── 8 Dokumentation ────────────────────────────────────────────────────

    kapitel('8', 'Dokumentation und Nachweise');
    liste([
        'Bautagebuch',
        'Schleusenbuch mit Personen sowie Eintritts- und Austrittszeit',
        'Filterbuch bei Filtergeräten, die nicht täglich gewechselt werden',
        'Nachweis der Tragezeiten und Erholungszeiten (DGUV Regel 112-190)',
        'Dokumentation der Unterweisungen mit Datum, Inhalt und Unterschriften',
        'Nachweis der arbeitsmedizinischen Vorsorge je Beschäftigtem',
        'Messprotokolle nach dem Messkonzept',
        'Entsorgungsnachweise, Wiegescheine, Begleitscheine',
        'Anzeige nach Abschnitt 11.2 DGUV Regel 101-004',
        'Schriftliche Bestellung der Aufsichtsführenden mit Sachkundenachweis',
    ]);

    // Der Ablauf des Bodenmanagements steht hier und nicht in Kapitel 6: er
    // endet in der Nachweisfuehrung, und genau darum geht es in diesem
    // Kapitel. Auch er wird nur gedruckt, wenn er erfasst ist.
    const ablauf = skizzenParameter(b, 'ablauf', ABLAUF_STANDARD);
    if (ablauf) skizze(zeichneAblauf(ablauf));

    // ── 9 Unterlagen und Anlagen ───────────────────────────────────────────

    // Ob eine Unterlage vorliegt, entscheidet NICHT dieses Kapitel, sondern
    // `anlagen.ts` — dieselbe Regel, die auch die Karte „Druckstuecke und
    // Nachweise" anzeigt. Der Generator baut keine Fachlogik nach: im
    // Handbuch-Modul hat genau das einmal dazu gefuehrt, dass der Titel eines
    // admin-gesperrten Dokuments gedruckt wurde.
    kapitel('9', 'Verwendete Unterlagen und Anlagen');
    const grundlagen = anlagenZeilen(b.unterlagen, 'grundlage');
    const anlagen = anlagenZeilen(b.unterlagen, 'anlage');

    if (grundlagen.length > 0) {
        unterkapitel('Verwendete Unterlagen');
        tabelle(
            ['Herausgeber', 'Bezeichnung', 'Kennung', 'Datum', 'Nachweis'],
            grundlagen.map(z => [
                z.herausgeber, z.bezeichnung, z.kennung,
                z.datum ? datumDeutsch(z.datum) : FEHLT,
                z.nachweis,
            ]),
            [32, 55, 26, 22, 35],
        );
    }
    if (anlagen.length > 0) {
        unterkapitel('Anlagen');
        tabelle(
            ['Nr.', 'Bezeichnung', 'Datum', 'Nachweis'],
            anlagen.map(z => [
                z.nr,
                z.bezeichnung,
                z.datum ? datumDeutsch(z.datum) : FEHLT,
                z.nachweis,
            ]),
            [16, 92, 24, 38],
        );
    }
    // Regel 9: Der Plan zeigt auch das Offene. Was nicht in der Projektablage
    // belegt ist, steht namentlich im roten Kasten — eine Anlagenliste, in der
    // die Luecke nur eine Spalte weit auffaellt, wird ueberlesen.
    const offenerNachweis = offenerNachweisHinweis([...grundlagen, ...anlagen]);
    if (offenerNachweis) offenerPunkt(offenerNachweis);

    if (grundlagen.length === 0 && anlagen.length === 0) {
        offenerPunkt('OFFEN: Es sind keine Unterlagen benannt, auf die sich dieser Plan stützt.');
    }

    // ── 10 Offene Punkte ───────────────────────────────────────────────────

    const offene = PRUEFLISTE
        .map(p => ({ p, stand: b.pruefliste.find(x => x.check_key === p.key) }))
        .filter(x => x.stand && x.stand.status === 'maengel');

    if (offene.length > 0) {
        kapitel('10', 'Festgestellte Mängel aus der Selbstprüfung');
        tabelle(
            ['Prüfpunkt', 'Feststellung'],
            offene.map(x => [x.p.frage, x.stand?.bemerkung || NICHT_ANGEGEBEN]),
            [breite / 2, breite / 2],
        );
    }

    // ── Fuss ───────────────────────────────────────────────────────────────

    platz(ZEILE.text * 3);
    y += 4;
    setzeText(`Katalogfassung: ${plan.katalog_version}`, SCHRIFT.winzig, 'italic', GRAU);
    if (plan.historie.length > 0) {
        const letzte = plan.historie[plan.historie.length - 1];
        setzeText(
            `Letzte Änderung: ${datumDeutsch(letzte.datum)} — ${letzte.aenderungen} (${letzte.autor})`,
            SCHRIFT.winzig, 'italic', GRAU,
        );
    }

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    // Die FUSSZEILE bleibt unveraendert: dort sind die Pflichtangaben
    // ueblich und stoeren nicht — entfernt wird nur der Kopf.
    drawFooters(doc, company, lh, geo);
};

// ─── Kleinkram ──────────────────────────────────────────────────────────────

const datumDeutsch = (iso: string): string => {
    const t = (iso || '').slice(0, 10).split('-');
    return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : (iso || '');
};

const tiefe = (von: number | null, bis: number | null): string => {
    if (von === null && bis === null) return FEHLT;
    const z = (n: number | null) => (n === null ? '?' : String(n).replace('.', ','));
    return `${z(von)}–${z(bis)}`;
};

const artText = (art: string): string => ({
    regel: 'Aushub / Rückbau',
    vorbehalt: 'Analytik-Vorbehalt',
    abgegrenzt: 'nicht A+S-pflichtig',
    nebenarbeit: 'Nebenarbeit',
}[art] ?? art);

const messzielText = (ziel: string): string => ({
    akut: 'Akutgefahr (kontinuierlich)',
    chronisch: 'chronisch wirkende Stoffe',
    wirksamkeit: 'Wirksamkeit der Maßnahmen',
    grenzwert: 'Grenzwerteinhaltung',
}[ziel] ?? (ziel || 'OHNE ZIEL'));

/**
 * Der gespeicherte Parametersatz einer Skizze — `null`, wenn keiner erfasst
 * ist.
 *
 * Die Vorbelegung wird untergelegt und nicht ersetzt: einem aelteren
 * Datensatz, dem ein spaeter hinzugekommenes Feld fehlt, wuerde sonst ein
 * Schluessel fehlen, ueber den die Zeichenfunktion faellt.
 */
const skizzenParameter = <T,>(b: AsplanBundle, typ: SkizzenTyp, vorbelegung: T): T | null => {
    const s = b.skizzen.find(x => x.typ === typ);
    if (!s || Object.keys(s.parameter).length === 0) return null;
    return { ...vorbelegung, ...(s.parameter as unknown as T) };
};

/** Die Rasterskizze aus dem Buendel — `null`, wenn kein Feld verortet ist. */
const rasterAusBundle = (b: AsplanBundle): Zeichnung | null => {
    if (b.rasterfelder.length === 0) return null;
    const zuBereich = new Map<string, string>();
    for (const c of b.cluster) for (const f of c.felder) zuBereich.set(f, c.kennung);
    const abgegrenzt = new Set(
        b.cluster.filter(c => c.art === 'abgegrenzt').flatMap(c => c.felder));

    const felder = b.rasterfelder.map(f => {
        const proben = b.proben.filter(p => p.rasterfeld_id === f.id);
        // Ueber den RANG und nicht ueber die Zeichenkette: '>' verglich hier
        // alphabetisch. Eine Altprobe 'Z2' schlug damit 'BM-F3', und weil
        // `ebvKlasse('Z2')` null ist, wurde das Feld grau und mit „(offen)"
        // gedruckt — auf dem Blatt, nach dem gearbeitet wird.
        const beste = hoechsteEinstufung(proben.map(p => p.einstufung));
        const vorbehalt = proben.some(p => p.vorbehalt);
        return {
            feld_nr: f.feld_nr,
            spalte: f.raster_spalte,
            zeile: f.raster_zeile,
            einstufung: beste,
            vorbehalt,
            cluster_kennung: zuBereich.get(f.feld_nr),
            abgegrenzt: abgegrenzt.has(f.feld_nr),
        };
    });

    if (felder.every(f => f.spalte === null || f.zeile === null)) return null;
    return zeichneRaster(felder);
};

// ═══════════════════════════════════════════════════════════════════════════

/**
 * Der A+S-Plan als jsPDF-Dokument.
 *
 * Gibt bewusst das Dokument zurueck und keine Bytes: der Aufrufer entscheidet,
 * ob er speichert, anhaengt oder in die Vorschau haengt.
 */
export const generateAsplanPdf = (
    bundle: AsplanBundle,
    company: CompanyInfo,
    optionen?: AsplanPdfOptionen,
): jsPDF => {
    const doc = optionen && optionen.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    renderAsplan(doc, bundle, company || {}, optionen);
    return doc;
};

/**
 * Dateiname des Plans.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage-Bucket, und dort ist ein Schraegstrich aus einem
 * Projektnamen ein neuer Ordner.
 */
export const asplanDateiname = (
    projektName: string,
    revLabel?: string,
    datumIso?: string,
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['A+S-Plan', sauber(projektName)];
    if (revLabel) teile.push(sauber(revLabel));
    teile.push((datumIso || '').slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};
